/**
 * Runtime shell configuration — the defaults in `shellConfig.json` (the ONE
 * source, also read by the backend), overridden per application by
 * `application.shell`, and the layout a user ends up with.
 *
 * Nothing here is a literal: layout ids, limits and defaults come from the
 * JSON, so adding a layout or changing a cap is a data change.
 */
import SHELL_CONFIG from './shellConfig.json'

export type LayoutId = 'classic' | 'rail' | 'megamenu' | 'launchpad' | 'desktop-mac' | 'flat' | 'office'

export interface LayoutDescriptor { id: LayoutId; label: string; description: string }

interface ShellConfigFile {
  layouts: LayoutDescriptor[]
  defaultLayout: LayoutId
  recentLimit: number
  pinnedLimit: number
  phoneBreakpoint: number
  editors: {
    maxSessions: number
    snapshotMaxAgeHours: number
    snapshotDebounceMs: number
    lockTtlSeconds: number
  }
  /** Per-layout setting defaults (the macOS desktop's Dock side, …). */
  layoutDefaults?: Partial<Record<LayoutId, Record<string, string | number | boolean>>>
  limits: {
    layoutPrefKeysMax?: number
    layoutPrefStringMax?: number
    wallpaperMaxBytes?: number
    recentLimitMax: number
    pinnedLimitMax: number
    maxSessionsMax: number
    snapshotMaxAgeHoursMax: number
    lockTtlSecondsMin: number
    lockTtlSecondsMax: number
  }
}

const FILE = SHELL_CONFIG as unknown as ShellConfigFile

/** Every layout the shell knows, in menu order. */
export const LAYOUTS: LayoutDescriptor[] = FILE.layouts

export const LAYOUT_IDS: LayoutId[] = FILE.layouts.map((l) => l.id)

/** Caps for per-layout settings and the desktop picture (shellConfig.json). */
export const SHELL_LIMITS = {
  layoutPrefKeysMax: FILE.limits.layoutPrefKeysMax ?? 20,
  layoutPrefStringMax: FILE.limits.layoutPrefStringMax ?? 200,
  wallpaperMaxBytes: FILE.limits.wallpaperMaxBytes ?? 1572864,
}

/** A layout's own setting defaults (data in shellConfig.json). */
export function layoutDefaults(id: LayoutId): Record<string, string | number | boolean> {
  return { ...(FILE.layoutDefaults?.[id] || {}) }
}

export function isLayoutId(v: unknown): v is LayoutId {
  return typeof v === 'string' && (LAYOUT_IDS as string[]).includes(v)
}

/** `application.shell` as authored (every key optional). */
export interface AppShellSettings {
  defaultLayout?: string
  allowedLayouts?: string[]
  recentLimit?: number
  pinnedLimit?: number
  editors?: {
    maxSessions?: number
    snapshotMaxAgeHours?: number
    lockTtlSeconds?: number
  }
}

export interface ResolvedShellConfig {
  defaultLayout: LayoutId
  allowedLayouts: LayoutId[]
  recentLimit: number
  pinnedLimit: number
  phoneBreakpoint: number
  editors: {
    maxSessions: number
    snapshotMaxAgeHours: number
    snapshotDebounceMs: number
    lockTtlSeconds: number
  }
}

function clampInt(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  if (!Number.isFinite(n)) return fallback
  return Math.max(min, Math.min(max, Math.round(n)))
}

/** The application's settings over the file defaults, clamped to the file limits. */
export function resolveShellConfig(app?: AppShellSettings | null): ResolvedShellConfig {
  const a = (app && typeof app === 'object') ? app : {}
  const allowedRaw = Array.isArray(a.allowedLayouts) ? a.allowedLayouts.filter(isLayoutId) : []
  // An empty / absent allow-list means every layout. `classic` is always
  // reachable: it is the phone-width fallback and the layout every user had.
  const allowed: LayoutId[] = allowedRaw.length
    ? LAYOUT_IDS.filter((id) => id === 'classic' || allowedRaw.includes(id))
    : [...LAYOUT_IDS]
  const appDefault = isLayoutId(a.defaultLayout) && allowed.includes(a.defaultLayout)
    ? a.defaultLayout : null
  const lim = FILE.limits
  const ed = a.editors && typeof a.editors === 'object' ? a.editors : {}
  return {
    defaultLayout: appDefault || (allowed.includes(FILE.defaultLayout) ? FILE.defaultLayout : 'classic'),
    allowedLayouts: allowed,
    recentLimit: clampInt(a.recentLimit, FILE.recentLimit, 1, lim.recentLimitMax),
    pinnedLimit: clampInt(a.pinnedLimit, FILE.pinnedLimit, 1, lim.pinnedLimitMax),
    phoneBreakpoint: FILE.phoneBreakpoint,
    editors: {
      maxSessions: clampInt(ed.maxSessions, FILE.editors.maxSessions, 1, lim.maxSessionsMax),
      snapshotMaxAgeHours: clampInt(ed.snapshotMaxAgeHours, FILE.editors.snapshotMaxAgeHours, 1,
        lim.snapshotMaxAgeHoursMax),
      snapshotDebounceMs: FILE.editors.snapshotDebounceMs,
      lockTtlSeconds: clampInt(ed.lockTtlSeconds, FILE.editors.lockTtlSeconds,
        lim.lockTtlSecondsMin, lim.lockTtlSecondsMax),
    },
  }
}

/**
 * Which layout this user sees: their own choice when the application still
 * offers it, else the application default, else `classic`.
 */
export function resolveLayout(userChoice: unknown, cfg: ResolvedShellConfig): LayoutId {
  if (isLayoutId(userChoice) && cfg.allowedLayouts.includes(userChoice)) return userChoice
  if (cfg.allowedLayouts.includes(cfg.defaultLayout)) return cfg.defaultLayout
  return 'classic'
}

export function layoutLabel(id: LayoutId): string {
  return LAYOUTS.find((l) => l.id === id)?.label || id
}
